import React from "react";
import { motion } from "framer-motion";
import { Monitor, EyeOff, Network, ChevronRight } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Card } from "../../components/primitives";
import { NO_POSITION, OTHER } from "./teamInsights";

// The band that opens the Team page: what this team is made of, how much of it
// TeamGrid actually covers, and what needs a nudge. Every figure is derived from
// the same filtered member set the list below renders, so the two can't disagree.

const RING = { size: 128, stroke: 15 };

// A gap ("No position") is a neutral, and the roll-up bucket is a plain grey —
// neither is an identity, so neither earns a series hue.
function sliceColor(t, slice, i) {
  if (slice.key === NO_POSITION) return t.c.borderStrong;
  if (slice.key === OTHER) return t.status.neutral;
  return t.series[i % t.series.length];
}

function Donut({ composition }) {
  const t = useDashTheme();
  const { size, stroke } = RING;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const cx = size / 2;
  // A gap between arcs is what makes adjacent slices legible; below ~2% of the
  // circle the gap would eat the arc, so tiny slices keep a hairline instead.
  const GAP = 3;

  let offset = 0;
  const arcs = composition.slices.map((s, i) => {
    const len = s.share * c;
    const drawn = Math.max(len - GAP, 1);
    const arc = { key: s.key, color: sliceColor(t, s, i), drawn, offset, label: s.label, count: s.count };
    offset += len;
    return arc;
  });

  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ display: "block", transform: "rotate(-90deg)" }} aria-hidden="true">
        <circle cx={cx} cy={cx} r={r} fill="none" stroke={t.c.surfaceHover} strokeWidth={stroke} />
        {arcs.map((a, i) => (
          <motion.circle
            key={a.key}
            cx={cx} cy={cx} r={r} fill="none"
            stroke={a.color} strokeWidth={stroke} strokeLinecap="butt"
            strokeDasharray={`${a.drawn} ${c - a.drawn}`}
            initial={{ strokeDashoffset: -a.offset, opacity: 0 }}
            animate={{ strokeDashoffset: -a.offset, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.04 * i, ease: t.motion.ease.out }}
          >
            <title>{`${a.label}: ${a.count}`}</title>
          </motion.circle>
        ))}
      </svg>
      <div style={{
        position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", pointerEvents: "none",
      }}>
        <span style={{
          fontSize: 26, fontWeight: t.font.weight.semibold, letterSpacing: "-0.02em",
          lineHeight: 1, fontVariantNumeric: "tabular-nums", color: t.c.text,
        }}>{composition.total}</span>
        <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 3 }}>
          {composition.total === 1 ? "person" : "people"}
        </span>
      </div>
    </div>
  );
}

function ZoneTitle({ children, note }) {
  const t = useDashTheme();
  return (
    <div style={{
      display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8,
      margin: `0 0 ${t.s[4]}px`,
    }}>
      <span style={{
        fontSize: t.font.size.xs, fontWeight: t.font.weight.semibold, letterSpacing: "0.06em",
        textTransform: "uppercase", color: t.c.textTertiary,
      }}>{children}</span>
      {note && <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{note}</span>}
    </div>
  );
}

// One pip per person reads as headcount, which is the point of the control.
// Past ~24 people the pips go sub-pixel, so it becomes a plain proportion bar.
const PIP_LIMIT = 24;

function Meter({ label, icon, value, total, color }) {
  const t = useDashTheme();
  const pct = total ? Math.round((value / total) * 100) : 0;
  const track = t.c.surfaceHover;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: t.font.size.sm, color: t.c.textSecondary, minWidth: 0 }}>
          <span style={{ display: "inline-flex", color: t.c.icon, flexShrink: 0 }}>{icon}</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        </span>
        <span style={{ fontSize: t.font.size.sm, fontWeight: t.font.weight.semibold, color: t.c.text, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
          {value}<span style={{ color: t.c.textTertiary, fontWeight: t.font.weight.regular }}>/{total}</span>
          <span style={{ color: t.c.textTertiary, fontWeight: t.font.weight.regular }}> · </span>{pct}%
        </span>
      </div>
      <div style={{ display: "flex", gap: 3, height: 8 }} aria-hidden="true">
        {total > 0 && total <= PIP_LIMIT
          ? Array.from({ length: total }, (_, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ duration: 0.2, delay: 0.012 * i }}
              style={{ flex: 1, borderRadius: 2, background: i < value ? color : track }}
            />
          ))
          : (
            <>
              <span style={{ flex: Math.max(value, 0), borderRadius: 2, background: color }} />
              <span style={{ flex: Math.max(total - value, 0), borderRadius: 2, background: track }} />
              {total === 0 && <span style={{ flex: 1, borderRadius: 2, background: track }} />}
            </>
          )}
      </div>
    </div>
  );
}

const TONE_KEY = { warning: "warning", negative: "negative", neutral: "neutral" };

function AttentionRow({ bucket, active, onClick }) {
  const t = useDashTheme();
  const tone = TONE_KEY[bucket.tone] || "neutral";
  const soft = t.status[`${tone}Soft`];
  const fg = tone === "neutral" ? t.c.textTertiary : t.status[tone];
  const [hover, setHover] = React.useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
        padding: "8px", border: "none", borderRadius: t.r.sm, cursor: "pointer",
        background: active ? t.c.primarySoft : (hover ? t.c.surfaceMuted : "transparent"),
        color: t.c.text, fontFamily: t.font.family, fontSize: t.font.size.md,
        transition: "background 140ms ease",
      }}
    >
      <span style={{
        minWidth: 26, height: 22, padding: "0 6px", borderRadius: t.r.sm,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: t.font.size.sm, fontWeight: t.font.weight.semibold, flexShrink: 0,
        fontVariantNumeric: "tabular-nums", background: soft, color: fg,
      }}>{bucket.count}</span>
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {bucket.label}
      </span>
      <ChevronRight size={14} style={{ color: active ? t.c.primary : t.c.textTertiary, opacity: active || hover ? 1 : 0, flexShrink: 0, transition: "opacity 140ms" }} />
    </button>
  );
}

export function TeamPulse({ composition, coverage, attention, isActive, onToggle }) {
  const t = useDashTheme();
  const meterColor = { agent: t.c.primary, stealth: t.c.ai, placed: t.status.positive };
  const meterIcon = { agent: <Monitor size={13} />, stealth: <EyeOff size={13} />, placed: <Network size={13} /> };
  const actionable = attention.filter((b) => b.count > 0);
  const empty = composition.total === 0;

  return (
    <Card padding={0} style={{ marginBottom: t.s[4], "--tg-team-rule": t.c.border }}>
      <div className="tg-team-pulse">

        <section>
          {/* Always name the grouping — it adapts to what the org fills in, so
              an unlabelled ring would imply a breakdown it isn't showing. */}
          <ZoneTitle note={`by ${composition.groupedBy}`}>
            Team composition
          </ZoneTitle>
          {empty ? (
            <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, paddingBottom: t.s[4] }}>
              No members match the current filters.
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: t.s[5] }}>
              <Donut composition={composition} />
              <div style={{ display: "flex", flexDirection: "column", gap: 7, minWidth: 0, flex: 1 }}>
                {composition.slices.map((s, i) => (
                  <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: t.font.size.sm, minWidth: 0 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: sliceColor(t, s, i), flexShrink: 0 }} />
                    <span
                      title={s.label}
                      style={{
                        flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        color: s.isGap ? t.c.textTertiary : t.c.textSecondary,
                      }}
                    >{s.label}</span>
                    <span style={{ fontWeight: t.font.weight.semibold, color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{s.count}</span>
                    <span style={{ width: 34, textAlign: "right", fontSize: t.font.size.xs, color: t.c.textTertiary, fontVariantNumeric: "tabular-nums" }}>
                      {Math.round(s.share * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section>
          <ZoneTitle note="share of the team">Coverage</ZoneTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            {coverage.map((m) => (
              <Meter key={m.key} label={m.label} icon={meterIcon[m.key]} value={m.value} total={m.total} color={meterColor[m.key] || t.c.primary} />
            ))}
          </div>
        </section>

        <section>
          <ZoneTitle>Needs attention</ZoneTitle>
          {actionable.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: t.font.size.sm, color: t.c.textSecondary }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: t.status.positive, flexShrink: 0 }} />
              Everyone is set up — nothing needs a nudge.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 2, margin: "-6px -8px 0" }}>
              {actionable.map((b) => (
                <AttentionRow
                  key={b.key}
                  bucket={b}
                  active={isActive(b)}
                  onClick={() => onToggle(b)}
                />
              ))}
            </div>
          )}
        </section>

      </div>
    </Card>
  );
}
